import { cva, type VariantProps } from 'class-variance-authority';
import { CheckCircle2, Info, AlertTriangle, XCircle } from 'lucide-react';
import type React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center gap-2 rounded-lg px-2 py-1 text-xs font-medium w-fit',
  {
    variants: {
      variant: {
        success: 'bg-green-500/10 text-green-600',
        info: 'bg-blue-500/10 text-blue-600',
        warning: 'bg-yellow-400/10 text-yellow-700',
        error: 'bg-red-500/10 text-red-600',
        default: 'bg-muted text-foreground',
      },
    },
    defaultVariants: {
      variant: 'success',
    },
  },
);

const badgeIcons = {
  success: <CheckCircle2 className='text-green-500 size-4' />,
  info: <Info className='text-blue-500 size-4' />,
  warning: <AlertTriangle className='text-yellow-500 size-4' />,
  error: <XCircle className='text-red-500 size-4' />,
  default: null,
};

interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
}

export function Badge({
  className,
  variant,
  icon,
  children,
  ...props
}: BadgeProps) {
  const defaultIcon = variant ? badgeIcons[variant] : badgeIcons.success;

  return (
    <span className={cn(badgeVariants({ variant, className }))} {...props}>
      {icon === undefined ? defaultIcon : icon}
      <span>{children}</span>
    </span>
  );
}
